// src/contexts/AuthContext.js
import React, { createContext, useState, useCallback, useEffect } from 'react';
import ServiceFactory from '../shared/services/ServiceFactory';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const service = ServiceFactory.getService();

    const login = useCallback(async (credentials) => {
        try {
            const AuthenticationResult = await service.login(credentials);
            setIsAuthenticated(true);
            localStorage.setItem("IdToken", AuthenticationResult.IdToken || '');
            localStorage.setItem("AccessToken", AuthenticationResult.AccessToken || '');
            localStorage.setItem("RefreshToken", AuthenticationResult.RefreshToken || '');

            const currentUser = await service.getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
            }
            return currentUser;
        } catch (error) {
            throw error;
        }
    }, [service]);

    const logout = useCallback(async () => {
        setIsLoading(true);
        try {
            await service.logout();
            // Clear all auth-related state and storage
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem("IdToken");
            localStorage.removeItem("AccessToken");
            localStorage.removeItem("RefreshToken");
        } catch (error) {
            console.error('Logout error:', error);
            // Still clear everything on error
            setUser(null);
            setIsAuthenticated(false);
            localStorage.removeItem("IdToken");
            localStorage.removeItem("AccessToken");
            localStorage.removeItem("RefreshToken");
        } finally {
            setIsLoading(false);
        }
    }, [service]);

    const signUp = useCallback(async (credentials) => {
        try {
            const AuthenticationResult = await service.signUp(credentials);
            return AuthenticationResult;
        } catch (error) {
            throw error;
        }
    }, [service]);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setIsLoading(true);
                const AccessToken = localStorage.getItem("AccessToken");
                const RefreshToken = localStorage.getItem("RefreshToken");

                // Only try to get current user if we have tokens
                if ((AccessToken || RefreshToken) && !user) {
                    try {
                        // getCurrentUser will automatically try to refresh expired tokens
                        const currentUser = await service.getCurrentUser();
                        if (currentUser) {
                            setUser(currentUser);
                            setIsAuthenticated(true);
                        } else {
                            // If getCurrentUser returns null (refresh failed), clear everything
                            setUser(null);
                            setIsAuthenticated(false);
                            localStorage.removeItem("IdToken");
                            localStorage.removeItem("AccessToken");
                            localStorage.removeItem("RefreshToken");
                        }
                    } catch (error) {
                        console.error('Failed to get current user:', error);
                        // Clear auth state on error
                        setUser(null);
                        setIsAuthenticated(false);
                        localStorage.removeItem("IdToken");
                        localStorage.removeItem("AccessToken");
                        localStorage.removeItem("RefreshToken");
                    }
                } else if (!AccessToken && !RefreshToken) {
                    // No tokens found, ensure we're in a logged-out state
                    setUser(null);
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                setUser(null);
                setIsAuthenticated(false);
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);


    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated,
            isLoading,
            login,
            logout,
            signUp
        }}>
            {children}
        </AuthContext.Provider>
    );
};
